import type { CSSProperties, ReactElement } from 'react';
import { Group, Text, UnstyledButton } from '@mantine/core';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';
import type { SortDir } from './useRankedSort';

interface SortableColumnHeaderProps {
    label: string;
    /** True when this column drives the current sort. */
    active: boolean;
    /** Active sort direction (only meaningful when `active`). */
    dir: SortDir;
    onSort: () => void;
    /** Match the alignment of the column beneath — numeric columns are right-aligned. */
    align?: 'left' | 'right';
    /** Width / flex styling to match the column it sits above. */
    style?: CSSProperties;
}

/**
 * A clickable column header for {@link RankedList}. Mirrors the static header
 * look (uppercase, dimmed, 9px) and adds a sort affordance: a faint up/down
 * glyph when inactive, a solid arrow in the active direction.
 */
export default function SortableColumnHeader({ label, active, dir, onSort, align = 'left', style }: SortableColumnHeaderProps): ReactElement {
    const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ChevronUp : ChevronDown;
    const glyph = <Icon size={11} color="var(--bm-color-table-header-text)" style={{ flexShrink: 0, opacity: active ? 1 : 0.55 }} />;
    const text = (
        <Text fw={700} tt="uppercase" style={{ fontSize: '9px', letterSpacing: '0.06em', color: 'var(--bm-color-table-header-text)', opacity: active ? 1 : 0.7 }}>
            {label}
        </Text>
    );
    return (
        <UnstyledButton onClick={onSort} aria-label={`Sort by ${label}`} style={{ ...style, cursor: 'pointer' }}>
            <Group gap={4} wrap="nowrap" align="center" justify={align === 'right' ? 'flex-end' : 'flex-start'}>
                {align === 'right' ? (
                    <>
                        {glyph}
                        {text}
                    </>
                ) : (
                    <>
                        {text}
                        {glyph}
                    </>
                )}
            </Group>
        </UnstyledButton>
    );
}
